import React, { useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Grid, Row, Col, Button } from '@sm/wds-react';
import { t, T } from '@sm/intl';

import { NAV_VARIANT } from '../../enums';
import getClickHandlers from '../../helpers/getClickHandlers';
import { getLinkGenerator } from '../../helpers/logging';
import addScrollListener from './helpers/scrollListener';
import { MENU } from './helpers/enums';
import logger from './helpers/logger';
import COPY from './helpers/copy';
import MENU_CONFIG from './helpers/menuConfig';
import mrxLogoDark from './helpers/logo-marketresearch.svg';
import mrxLogoLight from './helpers/logo-marketresearch--white.svg';
import ExpandableMenu from './ExpandableMenu';

export const TEST_ID = 'NavForDesktop__MRX';

const getLink = getLinkGenerator({
  ut_source: 'market_research',
  ut_source2: 'solutions',
  ut_source3: 'subnav'
});

const NavForDesktop = ({ openMenu, setOpenMenu }) => {
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const navId = 'sm-mrx-nav-id';
  // Need to add this after layout is painted to fix flickering for MRX-21
  useLayoutEffect(
    addScrollListener(navId, isAtTop => setScrolledToTop(isAtTop))
  );
  const classes = classnames({
    'sm-mrx-nav__wrapper': true,
    'sm-mrx-nav--scroll': !scrolledToTop
  });
  const image = scrolledToTop ? mrxLogoLight : mrxLogoDark;
  const buttonColor = scrolledToTop ? 'muted' : 'upgrade';
  const buttonVariant = !scrolledToTop ? 'solid' : 'ghost';
  const buttonClass = classnames({
    'sm-mrx-nav__cta-button--light': scrolledToTop,
    'sm-mrx-nav__cta-button': true
  });
  const textVariant = scrolledToTop ? 'light' : 'dark';
  const onBlur = () => setOpenMenu(null);

  return (
    <div className={classes} id={navId}>
      <Grid noBleeds noGutters className="sm-mrx-nav" data-testid={TEST_ID}>
        <Row>
          <Col className="sm-mrx-nav__menu-col" xl={4}>
            <a
              href={getLink('/market-research')}
              aria-label={t(COPY.homeLink)}
              {...getClickHandlers(() => {
                logger.click.navigateTo('mrx_home');
              })}
            >
              <img
                src={image}
                alt={t(COPY.marketResearchSolutions)}
                className="sm-mrx-nav__logo"
              />
            </a>
          </Col>
          <Col className="sm-mrx-nav__menu-col" xl={6}>
            <div className="sm-mrx-nav__menu-item">
              <ExpandableMenu
                {...MENU_CONFIG.solutions}
                isOpen={openMenu === MENU.SOLUTIONS}
                onClick={() => setOpenMenu(MENU.SOLUTIONS)}
                onBlur={onBlur}
                variant={textVariant}
              />
            </div>
            <div className="sm-mrx-nav__menu-item">
              <ExpandableMenu
                {...MENU_CONFIG.services}
                variant={textVariant}
                onBlur={onBlur}
              />
            </div>
            <div className="sm-mrx-nav__menu-item sm-mrx-nav__menu-item--last">
              <ExpandableMenu
                {...MENU_CONFIG.moreResources}
                isOpen={openMenu === MENU.MORE_RESOURCES}
                onClick={() => setOpenMenu(MENU.MORE_RESOURCES)}
                onBlur={onBlur}
                variant={textVariant}
              />
            </div>
          </Col>
          <Col className="sm-mrx-nav__menu-col sm-mrx-nav__cta" xl={2}>
            <Button
              variant={buttonVariant}
              color={buttonColor}
              className={buttonClass}
              href={getLink('/market-research/get-a-demo', {
                ut_ctatext: 'Get a demo'
              })}
              {...getClickHandlers(() => {
                logger.click.navigateTo('get_a_demo');
              })}
            >
              <T desc={COPY.getADemo} />
            </Button>
          </Col>
        </Row>
      </Grid>
    </div>
  );
};

NavForDesktop.propTypes = {
  openMenu: PropTypes.oneOf(Object.values(MENU)),
  setOpenMenu: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(Object.values(NAV_VARIANT)).isRequired
};

NavForDesktop.defaultProps = {
  openMenu: undefined
};

export default NavForDesktop;
